// pages/ContactInfoPage/index.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage, useModal } from "context";
import { FormButton, FiltersDropdown } from "reusableComponents";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "store/auth/thunks";
import { updateUserData, updateBankDetails } from "services/dashboard.services";
import { validateEditUserDataFields } from "helpers/validation";
import { default as payLineCredit } from "assets/images/payline-credit.svg";
import styles from "./styles.module.scss";
import CountryCodeSelector from "./components/CountryCodeSelector";
import CountryDropdown from "./components/CountryDropdown";
import { countryCodes } from "./components/CountryCodeSelector/countryCodes";

const ContactInfoPage = () => {
  const { t } = useTranslation("global");
  const { language } = useLanguage();
  const { openModal } = useModal();
  const dispatch = useDispatch();

  const token = useSelector((state) => state.auth.token);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const userType = useSelector((state) => state.auth.userType);
  
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  // Determine user type
  const isProvider = userType === "provider";
  const isCompany = isProvider || currentUser?.status === "company";
  const isIndividualSeeker = userType === "seeker" && currentUser?.status !== "company";
  const showBankDetails = isProvider && currentUser?.profile_completed === true;
  const showMultipleContacts = isProvider || isCompany;

  // Personal Info State (Individual Seeker)
  const [personalFormValues, setPersonalFormValues] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    position: "",
    address: "",
    city: "",
    zipCode: "",
    fax: "",
  });

  // Personal Info State with Contact 1 & 2 (for providers/company)
  const [multiContactFormValues, setMultiContactFormValues] = useState({
    first_name: "",
    last_name: "",
    contact_phone: "",
    function: "",
    second_first_name: "",
    second_last_name: "",
    second_contact_phone: "",
    second_function: "",
  });

  // Company Info State
  const [companyFormValues, setCompanyFormValues] = useState({
    country_of_residence: "",
    social_reason: "",
    company_number: "",
    vat_number: "",
    vat_taxable_person: false,
    company_address: {
      address: "",
      city: "",
      postal_code: "",
    },
    phone: "",
    contact_fax: "",
    currency: "978",
  });

  // Bank Details State
  const [bankFormValues, setBankFormValues] = useState({
    bank_bic: "",
    bank_iban: "",
    bank_owner: "",
  });

  // Country of residence state (for company form)
  const [selectedResidenceCountry, setSelectedResidenceCountry] = useState(null);

  // Phone state for personal (individual seeker)
  const [selectedPersonalCountry, setSelectedPersonalCountry] = useState(
    countryCodes.find(country => country.code === '+33') || countryCodes[0]
  );
  const [personalPhoneValue, setPersonalPhoneValue] = useState('');

  // Phone state for multi-contact - Contact 1
  const [selectedContact1Country, setSelectedContact1Country] = useState(
    countryCodes.find(country => country.code === '+33') || countryCodes[0]
  );
  const [contact1PhoneValue, setContact1PhoneValue] = useState('');

  // Phone state for multi-contact - Contact 2
  const [selectedContact2Country, setSelectedContact2Country] = useState(
    countryCodes.find(country => country.code === '+33') || countryCodes[0]
  );
  const [contact2PhoneValue, setContact2PhoneValue] = useState('');

  // Phone state for company info
  const [selectedCompanyCountry, setSelectedCompanyCountry] = useState(
    countryCodes.find(country => country.code === '+33') || countryCodes[0]
  );
  const [companyPhoneValue, setCompanyPhoneValue] = useState('');

  // Currency options - Only EUR and GBP
  const currencyOptions = [
    { value: "978", label: "EUR" },
    { value: "826", label: "GBP" },
  ];

  // Detect account type for bank details
  const detectAccountType = (bankIban) => {
    const ibanPattern = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,}$/;
    if (ibanPattern.test(bankIban)) {
      return "iban";
    }
    return "bban";
  };

  // Load user data
  useEffect(() => {
    if (currentUser) {
      if (isIndividualSeeker) {
        setPersonalFormValues({
          first_name: currentUser?.first_name || "",
          last_name: currentUser?.last_name || "",
          phone: currentUser?.phone || "",
          position: currentUser?.position || "",
          address: currentUser?.address?.address || "",
          city: currentUser?.address?.city || "",
          zipCode: currentUser?.address?.postal_code || "",
          fax: currentUser?.fax || "",
        });

        const phone = currentUser?.phone || '';
        const matchedCountry = countryCodes.find(country => phone.startsWith(country.code));
        if (matchedCountry) {
          setSelectedPersonalCountry(matchedCountry);
          setPersonalPhoneValue(phone.replace(matchedCountry.code, '').trim());
        } else {
          setPersonalPhoneValue(phone);
        }
      }

      if (showMultipleContacts) {
        setMultiContactFormValues({
          first_name: currentUser?.first_name || "",
          last_name: currentUser?.last_name || "",
          contact_phone: currentUser?.contact_phone || "",
          function: currentUser?.function || "",
          second_first_name: currentUser?.second_first_name || "",
          second_last_name: currentUser?.second_last_name || "",
          second_contact_phone: currentUser?.second_contact_phone || "",
          second_function: currentUser?.second_function || "",
        });

        const contact1Phone = currentUser?.contact_phone || '';
        const matchedContact1 = countryCodes.find(country => contact1Phone.startsWith(country.code));
        if (matchedContact1) {
          setSelectedContact1Country(matchedContact1);
          setContact1PhoneValue(contact1Phone.replace(matchedContact1.code, '').trim());
        } else {
          setContact1PhoneValue(contact1Phone);
        }

        const contact2Phone = currentUser?.second_contact_phone || '';
        const matchedContact2 = countryCodes.find(country => contact2Phone.startsWith(country.code));
        if (matchedContact2) {
          setSelectedContact2Country(matchedContact2);
          setContact2PhoneValue(contact2Phone.replace(matchedContact2.code, '').trim());
        } else {
          setContact2PhoneValue(contact2Phone);
        }
      }

      if (isCompany) {
        const residenceCountry = currentUser?.country_of_residence || '';
        const matchedResidence = countryCodes.find(country => 
          country.name.toLowerCase() === residenceCountry.toLowerCase() ||
          country.code === residenceCountry
        );
        if (matchedResidence) {
          setSelectedResidenceCountry(matchedResidence);
        }

        setCompanyFormValues({
          country_of_residence: currentUser?.country_of_residence || "",
          social_reason: currentUser?.social_reason || "",
          company_number: currentUser?.company_number || "",
          vat_number: currentUser?.vat_number || "",
          vat_taxable_person: currentUser?.vat_taxable_person || false,
          company_address: {
            address: currentUser?.company_address?.address || "",
            city: currentUser?.company_address?.city || "",
            postal_code: currentUser?.company_address?.postal_code || "",
          },
          phone: currentUser?.phone || "",
          contact_fax: currentUser?.contact_fax || "",
          currency: currentUser?.currency || "978",
        });

        const companyPhone = currentUser?.phone || '';
        const matchedCompanyCountry = countryCodes.find(country => companyPhone.startsWith(country.code));
        if (matchedCompanyCountry) {
          setSelectedCompanyCountry(matchedCompanyCountry);
          setCompanyPhoneValue(companyPhone.replace(matchedCompanyCountry.code, '').trim());
        } else {
          setCompanyPhoneValue(companyPhone);
        }
      }

      if (showBankDetails) {
        setBankFormValues({
          bank_bic: currentUser?.bank_bic || "",
          bank_iban: currentUser?.bank_iban || "",
          bank_owner: currentUser?.bank_owner || "",
        });
      }
    }
  }, [currentUser, isIndividualSeeker, showMultipleContacts, isCompany, showBankDetails]);

  // Update phone values
  useEffect(() => {
    const fullPhone = personalPhoneValue ? `${selectedPersonalCountry.code} ${personalPhoneValue}` : '';
    if (fullPhone !== personalFormValues.phone) {
      setPersonalFormValues(prev => ({ ...prev, phone: fullPhone }));
    }
  }, [personalPhoneValue, selectedPersonalCountry]);

  useEffect(() => {
    const fullPhone = contact1PhoneValue ? `${selectedContact1Country.code} ${contact1PhoneValue}` : '';
    if (fullPhone !== multiContactFormValues.contact_phone) {
      setMultiContactFormValues(prev => ({ ...prev, contact_phone: fullPhone }));
    }
  }, [contact1PhoneValue, selectedContact1Country]);

  useEffect(() => {
    const fullPhone = contact2PhoneValue ? `${selectedContact2Country.code} ${contact2PhoneValue}` : '';
    if (fullPhone !== multiContactFormValues.second_contact_phone) {
      setMultiContactFormValues(prev => ({ ...prev, second_contact_phone: fullPhone }));
    }
  }, [contact2PhoneValue, selectedContact2Country]);

  useEffect(() => {
    const fullPhone = companyPhoneValue ? `${selectedCompanyCountry.code} ${companyPhoneValue}` : '';
    if (fullPhone !== companyFormValues.phone) {
      setCompanyFormValues(prev => ({ ...prev, phone: fullPhone }));
    }
  }, [companyPhoneValue, selectedCompanyCountry]);

  // Handlers
  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPersonalFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handlePersonalBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    const errorMessage = validateEditUserDataFields(name, value, t, personalFormValues);
    setErrors(prev => ({ ...prev, [name]: errorMessage || null }));
  };

  const handlePersonalPhoneChange = (value) => {
    setPersonalPhoneValue(value);
    setErrors(prev => ({ ...prev, phone: null }));
  };

  const handlePersonalPhoneBlur = () => {
    setTouchedFields(prev => ({ ...prev, phone: true }));
    const fullPhone = personalPhoneValue ? `${selectedPersonalCountry.code} ${personalPhoneValue}` : '';
    const errorMessage = validateEditUserDataFields('phone', fullPhone, t, personalFormValues);
    setErrors(prev => ({ ...prev, phone: errorMessage || null }));
  };

  const handleMultiContactChange = (e) => {
    const { name, value } = e.target;
    setMultiContactFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleMultiContactBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    const errorMessage = validateEditUserDataFields(name, value, t, multiContactFormValues);
    setErrors(prev => ({ ...prev, [name]: errorMessage || null }));
  };

  const handleContact1PhoneChange = (value) => {
    setContact1PhoneValue(value);
    setErrors(prev => ({ ...prev, contact_phone: null }));
  };

  const handleContact1PhoneBlur = () => {
    setTouchedFields(prev => ({ ...prev, contact_phone: true }));
    const fullPhone = contact1PhoneValue ? `${selectedContact1Country.code} ${contact1PhoneValue}` : '';
    const errorMessage = validateEditUserDataFields('contact_phone', fullPhone, t, multiContactFormValues);
    setErrors(prev => ({ ...prev, contact_phone: errorMessage || null }));
  };

  const handleContact2PhoneChange = (value) => {
    setContact2PhoneValue(value);
    setErrors(prev => ({ ...prev, second_contact_phone: null }));
  };

const handleContact2PhoneBlur = () => {
  setTouchedFields(prev => ({ ...prev, second_contact_phone: true }));
  const fullPhone = contact2PhoneValue ? `${selectedContact2Country.code} ${contact2PhoneValue}` : '';
  
  // Only validate if there's a value (optional field)
  if (fullPhone && fullPhone.trim() !== '') {
    const errorMessage = validateEditUserDataFields('second_contact_phone', fullPhone, t, multiContactFormValues);
    setErrors(prev => ({ ...prev, second_contact_phone: errorMessage || null }));
  } else {
    // Clear error if empty (optional)
    setErrors(prev => ({ ...prev, second_contact_phone: null }));
  }
};

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("company_address.")) {
      const [_, addressField] = name.split(".");
      setCompanyFormValues((prev) => ({
        ...prev,
        company_address: { ...prev.company_address, [addressField]: value },
      }));
    } else {
      setCompanyFormValues((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleCompanyBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };

  const handleCompanyPhoneChange = (value) => {
    setCompanyPhoneValue(value);
    setErrors(prev => ({ ...prev, phone: null }));
  };

  const handleCompanyPhoneBlur = () => {
    setTouchedFields(prev => ({ ...prev, phone: true }));
    const fullPhone = companyPhoneValue ? `${selectedCompanyCountry.code} ${companyPhoneValue}` : '';
    if (!fullPhone && isCompany) {
      setErrors(prev => ({ ...prev, phone: t("validation.required") }));
    }
  };

  const handleResidenceCountrySelect = (country) => {
    setSelectedResidenceCountry(country);
    setCompanyFormValues(prev => ({ ...prev, country_of_residence: country.name }));
    setErrors(prev => ({ ...prev, country_of_residence: null }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCompanyFormValues((prev) => ({
      ...prev,
      [name]: checked,
      ...(name === "vat_taxable_person" && !checked && { vat_number: "" }),
    }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleCurrencySelect = (value) => {
    setCompanyFormValues((prev) => ({ ...prev, currency: value }));
    setErrors((prev) => ({ ...prev, currency: null }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleBankBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    const errorMessage = validateEditUserDataFields(name, value, t, bankFormValues);
    setErrors(prev => ({ ...prev, [name]: errorMessage || null }));
  };

  // Validation functions
  const validatePersonalForm = () => {
    const newErrors = {};
    Object.keys(personalFormValues).forEach((key) => {
      const errorMessage = validateEditUserDataFields(key, personalFormValues[key], t, personalFormValues);
      if (errorMessage) newErrors[key] = errorMessage;
    });
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

const validateMultiContactForm = () => {
  const newErrors = {};
  
  // Required fields (Contact 1)
  const requiredFields = ['first_name', 'last_name', 'contact_phone', 'function'];
  
  requiredFields.forEach(key => {
    const errorMessage = validateEditUserDataFields(key, multiContactFormValues[key], t, multiContactFormValues);
    if (errorMessage) newErrors[key] = errorMessage;
  });
  
  // Optional fields (Contact 2) - only validate if they have a value
  const optionalFields = ['second_first_name', 'second_last_name', 'second_contact_phone', 'second_function'];
  
  optionalFields.forEach(key => {
    if (multiContactFormValues[key] && multiContactFormValues[key].trim() !== '') {
      const errorMessage = validateEditUserDataFields(key, multiContactFormValues[key], t, multiContactFormValues);
      if (errorMessage) newErrors[key] = errorMessage;
    }
  });
  
  setErrors(prev => ({ ...prev, ...newErrors }));
  return Object.keys(newErrors).length === 0;
};

  const validateCompanyForm = () => {
    const newErrors = {};
    const values = companyFormValues;
  
    if (!values.country_of_residence) newErrors.country_of_residence = t("validation.required");
    if (!values.social_reason) newErrors.social_reason = t("validation.required");
    if (!values.company_number) newErrors.company_number = t("validation.required");
    if (!values.vat_number && values.vat_taxable_person) newErrors.vat_number = t("validation.required");
    if (!values.company_address.address) newErrors["company_address.address"] = t("validation.required");
    if (!values.company_address.city) newErrors["company_address.city"] = t("validation.required");
    if (!values.company_address.postal_code) newErrors["company_address.postal_code"] = t("validation.required");
    if (!values.phone) newErrors.phone = t("validation.required");
    if (!values.currency) newErrors.currency = t("validation.required");
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  // Submit handlers
  const handlePersonalSubmit = async (e) => {
    e.preventDefault();
    if (!validatePersonalForm()) return;
    setIsLoading(true);
    try {
      const payload = {
        fax: personalFormValues.fax,
        phone: personalFormValues.phone,
        position: personalFormValues.position,
        last_name: personalFormValues.last_name,
        first_name: personalFormValues.first_name,
        address: {
          city: personalFormValues.city,
          address: personalFormValues.address,
          postal_code: personalFormValues.zipCode,
        },
      };
      const response = await updateUserData(payload, token, language);
      dispatch(getCurrentUser({ language }));
      openModal({
        content: <p>{response.message}</p>,
        title: null,
        type: "success",
        autoClose: true,
        closeDelay: 1200,
      });
    } catch (error) {
      if (error?.errors) setErrors(error.errors);
      else console.error("Unexpected error:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMultiContactSubmit = async (e) => {
    e.preventDefault();
    if (!validateMultiContactForm()) return;
    setIsLoading(true);
    try {
      const payload = {
        first_name: multiContactFormValues.first_name,
        last_name: multiContactFormValues.last_name,
        contact_phone: multiContactFormValues.contact_phone,
        function: multiContactFormValues.function,
        second_first_name: multiContactFormValues.second_first_name,
        second_last_name: multiContactFormValues.second_last_name,
        second_contact_phone: multiContactFormValues.second_contact_phone,
        second_function: multiContactFormValues.second_function,
      };
      const response = await updateUserData(payload, token, language);
      dispatch(getCurrentUser({ language }));
      openModal({
        content: <p>{response.message}</p>,
        title: null,
        type: "success",
        autoClose: true,
        closeDelay: 1200,
      });
    } catch (error) {
      if (error?.errors) setErrors(error.errors);
      else console.error("Unexpected error:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    if (!validateCompanyForm()) return;
    setIsLoading(true);
    try {
      const payload = {
        country_of_residence: companyFormValues.country_of_residence,
        social_reason: companyFormValues.social_reason,
        company_number: companyFormValues.company_number,
        vat_number: companyFormValues.vat_number,
        vat_taxable_person: companyFormValues.vat_taxable_person,
        company_address: {
          address: companyFormValues.company_address.address,
          city: companyFormValues.company_address.city,
          postal_code: companyFormValues.company_address.postal_code,
        },
        phone: companyFormValues.phone,
        contact_fax: companyFormValues.contact_fax,
        currency: companyFormValues.currency,
      };
      const response = await updateUserData(payload, token, language);
      dispatch(getCurrentUser({ language }));
      openModal({
        content: <p>{response.message}</p>,
        title: null,
        type: "success",
        autoClose: true,
        closeDelay: 1200,
      });
    } catch (error) {
      if (error?.errors) setErrors(error.errors);
      else console.error("Unexpected error:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    
    const bankErrors = {};
    Object.keys(bankFormValues).forEach((key) => {
      const errorMessage = validateEditUserDataFields(key, bankFormValues[key], t, bankFormValues);
      if (errorMessage) bankErrors[key] = errorMessage;
    });
    if (Object.keys(bankErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...bankErrors }));
      return;
    }
    
    setIsLoading(true);
    try {
      const accountType = detectAccountType(bankFormValues.bank_iban);
      const payload = {
        type: accountType,
        bank_iban: bankFormValues.bank_iban,
        bank_bic: bankFormValues.bank_bic,
        bank_owner: bankFormValues.bank_owner,
      };
      const response = await updateBankDetails(payload, token, language);
      dispatch(getCurrentUser({ language }));
      openModal({
        content: <p>{response.message}</p>,
        title: null,
        type: "success",
        autoClose: true,
        closeDelay: 1200,
      });
    } catch (error) {
      if (error?.errors) setErrors(error.errors);
      else console.error("Unexpected error:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Render functions
  const renderPersonalTextInput = (name, label, required = false) => (
    <div className={styles.inputGroup}>
      <label htmlFor={name}>
        {label} {required && <i>*</i>}
      </label>
      <div className={`${styles.inputWrapper} ${errors[name] ? styles.inputError : ''}`}>
        <input
          id={name}
          name={name}
          type="text"
          value={personalFormValues[name] || ''}
          onChange={handlePersonalChange}
          onBlur={handlePersonalBlur}
          placeholder={label}
          className={styles.textInput}
        />
      </div>
      {errors[name] && <span className={styles.errorMessage}>{errors[name]}</span>}
    </div>
  );

  const renderPersonalPhoneInput = () => (
    <div className={styles.inputGroup}>
      <label htmlFor="phone">
        {t("dashboard.contact.phone")} <i>*</i>
      </label>
      <div className={`${styles.phoneInputContainer} ${errors.phone ? styles.inputError : ''}`}>
        <CountryCodeSelector 
          selectedCountry={selectedPersonalCountry} 
          onCountrySelect={setSelectedPersonalCountry} 
        />
        <input
          id="phone"
          name="phone"
          type="tel"
          value={personalPhoneValue}
          onChange={(e) => handlePersonalPhoneChange(e.target.value)}
          onBlur={handlePersonalPhoneBlur}
          placeholder={t("dashboard.contact.phone")}
          className={styles.phoneInput}
        />
      </div>
      {errors.phone && <span className={styles.errorMessage}>{errors.phone}</span>}
    </div>
  );

  // Render functions for multi-contact - INLINE version (no label wrapper)
  const renderMultiContactTextInputInline = (name, label, required = false) => (
    <div className={styles.inputGroupInline}>
      <div className={`${styles.inputWrapper} ${errors[name] ? styles.inputError : ''}`}>
        <input
          id={name}
          name={name}
          type="text"
          value={multiContactFormValues[name] || ''}
          onChange={handleMultiContactChange}
          onBlur={handleMultiContactBlur}
          placeholder={label}
          className={styles.textInput}
        />
      </div>
      {errors[name] && <span className={styles.errorMessage}>{errors[name]}</span>}
    </div>
  );

  const renderContact1PhoneInputInline = () => (
    <div className={styles.inputGroupInline}>
      <div className={`${styles.phoneInputContainer} ${errors.contact_phone ? styles.inputError : ''}`}>
        <CountryCodeSelector 
          selectedCountry={selectedContact1Country} 
          onCountrySelect={setSelectedContact1Country} 
        />
        <input
          id="contact_phone"
          name="contact_phone"
          type="tel"
          value={contact1PhoneValue}
          onChange={(e) => handleContact1PhoneChange(e.target.value)}
          onBlur={handleContact1PhoneBlur}
          placeholder={t("dashboard.contact.phone")}
          className={styles.phoneInput}
        />
      </div>
      {errors.contact_phone && <span className={styles.errorMessage}>{errors.contact_phone}</span>}
    </div>
  );

  const renderContact2PhoneInputInline = () => (
    <div className={styles.inputGroupInline}>
      <div className={`${styles.phoneInputContainer} ${errors.second_contact_phone ? styles.inputError : ''}`}>
        <CountryCodeSelector 
          selectedCountry={selectedContact2Country} 
          onCountrySelect={setSelectedContact2Country} 
        />
        <input
          id="second_contact_phone"
          name="second_contact_phone"
          type="tel"
          value={contact2PhoneValue}
          onChange={(e) => handleContact2PhoneChange(e.target.value)}
          onBlur={handleContact2PhoneBlur}
          placeholder={t("dashboard.contact.phone")}
          className={styles.phoneInput}
        />
      </div>
      {errors.second_contact_phone && <span className={styles.errorMessage}>{errors.second_contact_phone}</span>}
    </div>
  );

  // Render functions for company info
  const renderCompanyInput = (name, label, required = false, placeholder = null) => {
    const isAddressField = name.startsWith("company_address.");
    const value = isAddressField 
      ? companyFormValues.company_address[name.split(".")[1]] 
      : companyFormValues[name];
    const error = errors[name];
    
    return (
      <div className={styles.inputGroup}>
        <label htmlFor={name}>
          {label} {required && <i>*</i>}
        </label>
        <div className={`${styles.inputWrapper} ${error ? styles.inputError : ''}`}>
          <input
            id={name}
            name={name}
            type="text"
            value={value || ''}
            onChange={handleCompanyChange}
            onBlur={handleCompanyBlur}
            placeholder={placeholder || label}
            className={styles.textInput}
          />
        </div>
        {error && <span className={styles.errorMessage}>{error}</span>}
      </div>
    );
  };

  const renderCompanyPhoneInput = () => (
    <div className={styles.inputGroup}>
      <label htmlFor="phone">
        {t("dashboard.contact.phone")} <i>*</i>
      </label>
      <div className={`${styles.phoneInputContainer} ${errors.phone ? styles.inputError : ''}`}>
        <CountryCodeSelector 
          selectedCountry={selectedCompanyCountry} 
          onCountrySelect={setSelectedCompanyCountry} 
        />
        <input
          id="phone"
          name="phone"
          type="tel"
          value={companyPhoneValue}
          onChange={(e) => handleCompanyPhoneChange(e.target.value)}
          onBlur={handleCompanyPhoneBlur}
          placeholder={t("dashboard.contact.phone")}
          className={styles.phoneInput}
        />
      </div>
      {errors.phone && <span className={styles.errorMessage}>{errors.phone}</span>}
    </div>
  );

  const renderCompanyCountryDropdown = () => (
    <div className={styles.inputGroup}>
      <label htmlFor="country_of_residence">
        {t("dashboard.contact.pays")} <i>*</i>
      </label>
      <CountryDropdown
        selectedCountry={selectedResidenceCountry}
        onCountrySelect={handleResidenceCountrySelect}
        placeholder={t("dashboard.contact.selectCountry") || "Select a country"}
        error={errors.country_of_residence}
        required
      />
      {errors.country_of_residence && (
        <span className={styles.errorMessage}>{errors.country_of_residence}</span>
      )}
    </div>
  );

  const renderCurrencyDropdown = () => (
    <div className={styles.inputGroup}>
      <label htmlFor="currency">
        {t("dashboard.contact.currency")} <i>*</i>
      </label>
      <FiltersDropdown
        options={currencyOptions}
        selected={companyFormValues.currency}
        onSelect={handleCurrencySelect}
        placeholder={t("form.currency")}
      />
      {errors.currency && <span className={styles.errorMessage}>{errors.currency}</span>}
    </div>
  );

  const renderBankInput = (name, label, required, placeholder) => (
    <div className={styles.inputGroup}>
      <label htmlFor={name}>
        {label} {required && <i>*</i>}
      </label>
      <div className={`${styles.inputWrapper} ${errors[name] ? styles.inputError : ''}`}>
        <input
          id={name}
          name={name}
          type="text"
          value={bankFormValues[name] || ''}
          onChange={handleBankChange}
          onBlur={handleBankBlur}
          placeholder={placeholder}
          className={styles.textInput}
        />
      </div>
      {errors[name] && <span className={styles.errorMessage}>{errors[name]}</span>}
    </div>
  );

  const renderBankDetailsMessage = () => (
    <div className={styles.formContainer}>
      <div className={styles.contactInfoMissing}>
        {t("dashboard.cards.contactInfoMissing")}
      </div>
    </div>
  );

  return (
    <div className={`${styles.pageContainer} ${isLoading ? styles.loading : ""}`}>
      
      {/* Important Section - Only for providers */}
      {isProvider && (
        <div className={styles.importantSection}>
          <h2 className={styles.importantTitle}  dangerouslySetInnerHTML={{
              __html: t("dashboard.contact.important")
            }}/>
          <p 
            className={styles.importantText}
            dangerouslySetInnerHTML={{
              __html: t("dashboard.contact.important-content")
            }}
          />
        </div>
      )}
      
      {/* ✅ Company Information Section - MOVED HERE (before Personal Info) */}
      {isCompany && (
        <>
          <h2 className={styles.pageTitle}>{t("dashboard.contact.company")}</h2>
          <form className={styles.formContainer} onSubmit={handleCompanySubmit}>
            <div className={styles.formGrid}>
              {renderCompanyInput("social_reason", t("dashboard.contact.companyName"), true)}
              {renderCompanyInput("company_number", t("dashboard.contact.companyNumber"), true)}
              {renderCompanyInput("company_address.address", t("dashboard.contact.address"), true)}
              {renderCompanyCountryDropdown()}
              {renderCompanyInput("company_address.city", t("dashboard.contact.city"), true)}
              {renderCompanyInput("company_address.postal_code", t("dashboard.contact.zipCode"), true)}
              {renderCompanyPhoneInput()}
              {renderCompanyInput("contact_fax", t("dashboard.contact.fax"), false)}
              
              <div className={styles.inputGroup}>
                <label htmlFor="vat_number">
                  {t("dashboard.contact.VATNumber")} {companyFormValues.vat_taxable_person && <i>*</i>}
                </label>
                <div className={`${styles.inputWrapper} ${errors.vat_number ? styles.inputError : ''}`}>
                  <input
                    id="vat_number"
                    name="vat_number"
                    type="text"
                    value={companyFormValues.vat_number || ''}
                    onChange={handleCompanyChange}
                    onBlur={handleCompanyBlur}
                    placeholder={t("dashboard.contact.VATNumber")}
                    className={styles.textInput}
                  />
                </div>
                {errors.vat_number && <span className={styles.errorMessage}>{errors.vat_number}</span>}
                <div className={styles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    name="vat_taxable_person"
                    checked={companyFormValues.vat_taxable_person}
                    onChange={handleCheckboxChange}
                    id="vat_taxable_person"
                  />
                  <label htmlFor="vat_taxable_person" className={styles.checkboxLabel}>
                    {t("dashboard.vatCheckboxName")}
                  </label>
                </div>
              </div>

              {/* Currency Field - Only for providers */}
              {isProvider && renderCurrencyDropdown()}
            </div>
            <div className={styles.submitButtonWrapper}>
              <FormButton
                label={t("buttons.save")}
                variant="primary"
                isLoading={isLoading}
              />
            </div>
          </form>
        </>
      )}

      {/* Personal Information Section - Individual Seeker */}
      {isIndividualSeeker && (
        <>
          <h2 className={styles.pageTitle}>{t("dashboard.contact.personal")}</h2>
          <form className={styles.formContainer} onSubmit={handlePersonalSubmit}>
            <div className={styles.formGrid}>
              {renderPersonalTextInput("first_name", t("dashboard.contact.firstName"), true)}
              {renderPersonalTextInput("last_name", t("dashboard.contact.lastName"), true)}
              {renderPersonalTextInput("position", t("dashboard.contact.job"))}
              {renderPersonalTextInput("address", t("dashboard.contact.address"))}
              {renderPersonalTextInput("city", t("dashboard.contact.city"))}
              {renderPersonalTextInput("zipCode", t("dashboard.contact.zipCode"))}
              {renderPersonalPhoneInput()}
              {renderPersonalTextInput("fax", t("dashboard.contact.fax"))}
            </div>
            <div className={styles.submitButtonWrapper}>
              <FormButton
                label={t("buttons.save")}
                variant="primary"
                isLoading={isLoading}
              />
            </div>
          </form>
        </>
      )}

      {/* Personal Information Section - Providers/Company (Contact 1 & 2 Side by Side) */}
      {showMultipleContacts && (
        <>
          <h2 className={styles.pageTitle}>{t("dashboard.contact.personal")}</h2>
          <form className={styles.formContainer} onSubmit={handleMultiContactSubmit}>
            
            <div className={styles.contactsTable}>
              {/* Row 1: Headers + First Name */}
              <div className={styles.contactFieldRow}>
                <div className={styles.contactFieldCell}>
                  <div className={styles.contactHeaderInline}>
                    <span className={styles.contactHeaderLabel}>
                      {t("dashboard.contact.contact1") || "Contact n⚬1"} <i>*</i>
                    </span>
                    <div className={styles.inputGroupInline}>
                      <div className={`${styles.inputWrapper} ${errors.first_name ? styles.inputError : ''}`}>
                        <input
                          id="first_name"
                          name="first_name"
                          type="text"
                          value={multiContactFormValues.first_name || ''}
                          onChange={handleMultiContactChange}
                          onBlur={handleMultiContactBlur}
                          placeholder={t("dashboard.contact.firstName")}
                          className={styles.textInput}
                        />
                      </div>
                      {errors.first_name && <span className={styles.errorMessage}>{errors.first_name}</span>}
                    </div>
                  </div>
                </div>
                <div className={styles.contactDivider} />
                <div className={styles.contactFieldCell}>
                  <div className={styles.contactHeaderInline}>
                    <span className={styles.contactHeaderLabel}>
                      {t("dashboard.contact.contact2") || "Contact n⚬2"}
                    </span>
                    <div className={styles.inputGroupInline}>
                      <div className={`${styles.inputWrapper} ${errors.second_first_name ? styles.inputError : ''}`}>
                        <input
                          id="second_first_name"
                          name="second_first_name"
                          type="text"
                          value={multiContactFormValues.second_first_name || ''}
                          onChange={handleMultiContactChange}
                          onBlur={handleMultiContactBlur}
                          placeholder={t("dashboard.contact.firstName")}
                          className={styles.textInput}
                        />
                      </div>
                      {errors.second_first_name && <span className={styles.errorMessage}>{errors.second_first_name}</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Last Name (no headers) */}
              <div className={styles.contactFieldRow}>
                <div className={styles.contactFieldCell}>
                  <div className={styles.contactHeaderInline}>
                    <span className={styles.contactHeaderLabel}>&nbsp;</span>
                    <div className={styles.inputGroupInline}>
                      <div className={`${styles.inputWrapper} ${errors.last_name ? styles.inputError : ''}`}>
                        <input
                          id="last_name"
                          name="last_name"
                          type="text"
                          value={multiContactFormValues.last_name || ''}
                          onChange={handleMultiContactChange}
                          onBlur={handleMultiContactBlur}
                          placeholder={t("dashboard.contact.lastName")}
                          className={styles.textInput}
                        />
                      </div>
                      {errors.last_name && <span className={styles.errorMessage}>{errors.last_name}</span>}
                    </div>
                  </div>
                </div>
                <div className={styles.contactDivider} />
                <div className={styles.contactFieldCell}>
                  <div className={styles.contactHeaderInline}>
                    <span className={styles.contactHeaderLabel}>&nbsp;</span>
                    <div className={styles.inputGroupInline}>
                      <div className={`${styles.inputWrapper} ${errors.second_last_name ? styles.inputError : ''}`}>
                        <input
                          id="second_last_name"
                          name="second_last_name"
                          type="text"
                          value={multiContactFormValues.second_last_name || ''}
                          onChange={handleMultiContactChange}
                          onBlur={handleMultiContactBlur}
                          placeholder={t("dashboard.contact.lastName")}
                          className={styles.textInput}
                        />
                      </div>
                      {errors.second_last_name && <span className={styles.errorMessage}>{errors.second_last_name}</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Phone (no headers) */}
              <div className={styles.contactFieldRow}>
                <div className={styles.contactFieldCell}>
                  <div className={styles.contactHeaderInline}>
                    <span className={styles.contactHeaderLabel}>&nbsp;</span>
                    <div className={styles.inputGroupInline}>
                      {renderContact1PhoneInputInline()}
                    </div>
                  </div>
                </div>
                <div className={styles.contactDivider} />
                <div className={styles.contactFieldCell}>
                  <div className={styles.contactHeaderInline}>
                    <span className={styles.contactHeaderLabel}>&nbsp;</span>
                    <div className={styles.inputGroupInline}>
                      {renderContact2PhoneInputInline()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 4: Position (no headers) */}
              <div className={styles.contactFieldRow}>
                <div className={styles.contactFieldCell}>
                  <div className={styles.contactHeaderInline}>
                    <span className={styles.contactHeaderLabel}>&nbsp;</span>
                    <div className={styles.inputGroupInline}>
                      <div className={`${styles.inputWrapper} ${errors.function ? styles.inputError : ''}`}>
                        <input
                          id="function"
                          name="function"
                          type="text"
                          value={multiContactFormValues.function || ''}
                          onChange={handleMultiContactChange}
                          onBlur={handleMultiContactBlur}
                          placeholder={t("dashboard.contact.job")}
                          className={styles.textInput}
                        />
                      </div>
                      {errors.function && <span className={styles.errorMessage}>{errors.function}</span>}
                    </div>
                  </div>
                </div>
                <div className={styles.contactDivider} />
                <div className={styles.contactFieldCell}>
                  <div className={styles.contactHeaderInline}>
                    <span className={styles.contactHeaderLabel}>&nbsp;</span>
                    <div className={styles.inputGroupInline}>
                      <div className={`${styles.inputWrapper} ${errors.second_function ? styles.inputError : ''}`}>
                        <input
                          id="second_function"
                          name="second_function"
                          type="text"
                          value={multiContactFormValues.second_function || ''}
                          onChange={handleMultiContactChange}
                          onBlur={handleMultiContactBlur}
                          placeholder={t("dashboard.contact.job")}
                          className={styles.textInput}
                        />
                      </div>
                      {errors.second_function && <span className={styles.errorMessage}>{errors.second_function}</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.submitButtonWrapper}>
              <FormButton
                label={t("buttons.save")}
                variant="primary"
                isLoading={isLoading}
              />
            </div>
          </form>

          {/* Email Edit Note */}
          <div className={styles.emailEditNote}>
            <p 
              dangerouslySetInnerHTML={{
                __html: t("dashboard.contact.emailEditNote")
              }}
            />
          </div>
        </>
      )}

      {/* Bank Details Section - ONLY for providers with profile_completed */}
      {isProvider && (
        <>
          <h2 className={styles.pageTitle}>{t("dashboard.accounts.title")}</h2>
          {currentUser?.profile_completed ? (
            <form className={styles.formContainer} onSubmit={handleBankSubmit}>
              <div className={styles.formGrid}>
                {renderBankInput(
                  "bank_iban",
                  t("dashboard.accounts.iban"),
                  true,
                  "FR7630006000011234567890189 or 12345678901234567890"
                )}
                {renderBankInput(
                  "bank_bic",
                  t("dashboard.accounts.bic"),
                  true,
                  "AAAABBCCDDD"
                )}
                {renderBankInput(
                  "bank_owner",
                  t("dashboard.accounts.accountOwner"),
                  true,
                  "John Doe"
                )}
              </div>
              <div className={styles.submitButtonWrapper}>
                <FormButton
                  label={t("buttons.save")}
                  variant="primary"
                  isLoading={isLoading}
                />
              </div>
            </form>
          ) : (
            renderBankDetailsMessage()
          )}
          
          <span
            className={styles.secureWebsiteText}
            dangerouslySetInnerHTML={{
              __html: t("dashboard.cards.secureWebsite"),
            }}
          />
          <div className={styles.poweredByContainer}>
            <img
              className={styles.powerdByMngImg}
              src={payLineCredit}
              alt="Powered by Mangopay"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ContactInfoPage;